import * as Sentry from '@sentry/nextjs';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVEL_RANKING: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const canLog = (level: LogLevel): boolean => {
  // Adjust the level here to reduce logs while developing.
  return LOG_LEVEL_RANKING[level] >= LOG_LEVEL_RANKING.debug;
};

interface LogData {
  error?: unknown;
  [key: string]: unknown;
}

const REDACTED_KEYS = new Set([
  'password',
  'token',
  'secret',
  'googleIdToken',
  'facebookIdToken',
  'appleIdToken',
  'accessToken',
  'stripeToken',
]);

export class TaggedLogger {
  constructor(private readonly tag: string) {}

  public debug = (message: string, data?: LogData): void => {
    if (canLog('debug')) {
      this.logMessageToStdOut('debug', message, data);
    }
  };

  public info = (message: string, data?: LogData): void => {
    if (canLog('info')) {
      this.logMessageToStdOut('info', message, data);
    }
  };

  public warn = (message: string, data?: LogData): void => {
    Sentry.captureMessage(message, {
      level: 'warning',
      extra: TaggedLogger.redactSensitiveData(data),
    });

    if (canLog('warn')) {
      this.logMessageToStdOut('warn', message, data);
    }
  };

  public error = (message: string, data?: LogData): void => {
    const { error, ...restOfData } = data ?? {};

    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: TaggedLogger.redactSensitiveData({ message, ...restOfData }),
      });
    } else {
      Sentry.captureMessage(message, {
        extra: TaggedLogger.redactSensitiveData(data),
        level: 'error',
      });
    }

    if (canLog('error')) {
      this.logMessageToStdOut('error', message, data);
    }
  };

  private logMessageToStdOut(level: LogLevel, message: string, data?: LogData): void {
    const baseConsoleArgs = [`[${level}]`, `[${this.tag}]`, message];
    const consoleArgs = data
      ? [...baseConsoleArgs, JSON.stringify(data, TaggedLogger.replaceError, 2)]
      : baseConsoleArgs;

    console.info(...consoleArgs);
  }

  /**
   * Replacer function for JSON.stringify. Error doesn't stringify well, so we extract the message.
   */
  private static replaceError(this: unknown, _key: string, value: unknown): unknown {
    if (value instanceof Error) {
      return { message: value.message };
    }

    return value;
  }

  private static redactSensitiveData(data?: LogData): LogData | undefined {
    if (data) {
      return Object.keys(data).reduce<LogData>((acc, key) => {
        const value = data[key];

        return {
          ...acc,
          [key]: REDACTED_KEYS.has(key) ? '**REDACTED**' : value,
        };
      }, {});
    }

    return data;
  }
}
